import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const AVATAR_COLORS = [
  '#25D366','#128C7E','#075E54','#34B7F1',
  '#8B5CF6','#EC4899','#F59E0B','#EF4444',
  '#3B82F6','#10B981','#F97316','#06B6D4',
];

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

/* ──────────────────────────────────────────────────────────────────────────
   Profile Page
   – Every user can change their profile picture
   – Every user can change their avatar colour
   – Shows install app banner
────────────────────────────────────────────────────────────────────────── */
const Profile = () => {
  const { user, token, updateUser } = useAuth();
  const [uploading, setUploading]     = useState(false);
  const [colorSaving, setColorSaving] = useState(false);
  const [toast, setToast]             = useState(null);
  const [preview, setPreview]         = useState(null);
  const [imgError, setImgError]       = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled]     = useState(false);
  const fileInputRef = useRef(null);

  // Reset img error when user profile_picture changes
  useEffect(() => { setImgError(false); }, [user?.profile_picture]);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);
    if (window.__pwaInstallPrompt) setInstallPrompt(window.__pwaInstallPrompt);
    const onAvailable = () => setInstallPrompt(window.__pwaInstallPrompt);
    const onInstalled = () => { setIsInstalled(true); setInstallPrompt(null); };
    window.addEventListener('pwaInstallAvailable', onAvailable);
    window.addEventListener('pwaInstalled', onInstalled);
    return () => {
      window.removeEventListener('pwaInstallAvailable', onAvailable);
      window.removeEventListener('pwaInstalled', onInstalled);
    };
  }, []);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('error', 'Only image files are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('error', 'Image must be under 5 MB.');
      return;
    }
    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => { setPreview(reader.result); setImgError(false); };
    reader.readAsDataURL(file);
    handleUpload(file);
    e.target.value = '';
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file); // must match backend field name
      const res = await axios.put(`${API}/api/users/me/avatar`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      updateUser({ profile_picture: res.data.profile_picture });
      setPreview(null); // use the saved URL from server
      showToast('success', 'Profile picture updated!');
    } catch (err) {
      setPreview(null);
      showToast('error', err.response?.data?.message || 'Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const handleColorChange = async (color) => {
    if (colorSaving) return;
    setColorSaving(true);
    try {
      await axios.put(`${API}/api/users/me/color`, { avatar_color: color }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateUser({ avatar_color: color });
      showToast('success', 'Avatar colour updated!');
    } catch (err) {
      showToast('error', 'Failed to update colour.');
    } finally {
      setColorSaving(false);
    }
  };

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') { setIsInstalled(true); setInstallPrompt(null); }
    }
  };

  // Determine avatar source: local preview → server picture → initials
  const avatarSrc = preview
    ? preview
    : (!imgError && user?.profile_picture ? `${API}${user.profile_picture}` : null);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center py-6 sm:py-8 px-3 sm:px-4">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 sm:px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium transition-all max-w-[90vw] ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-whatsapp-green'
        }`}>
          <span>{toast.type === 'error' ? '⚠️' : '✅'}</span>
          <span>{toast.msg}</span>
          <button onClick={() => setToast(null)} className="opacity-70 hover:opacity-100 ml-1">✕</button>
        </div>
      )}

      <div className="w-full max-w-md space-y-4 sm:space-y-5">

        {/* Header */}
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl font-black text-whatsapp-darker">👤 My Profile</h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-0.5">Manage your account settings</p>
        </div>

        {/* ── Profile Card ───────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">

          {/* Avatar */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              <div
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden flex items-center justify-center text-white font-black text-2xl sm:text-3xl shadow-lg border-4 border-white ring-2 ring-whatsapp-green/30"
                style={{ backgroundColor: avatarSrc ? 'transparent' : (user?.avatar_color || '#25D366') }}
              >
                {avatarSrc ? (
                  <img
                    src={avatarSrc}
                    alt={user?.name}
                    className="w-full h-full object-cover"
                    onError={() => { setPreview(null); setImgError(true); }}
                  />
                ) : (
                  getInitials(user?.name)
                )}
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/50 rounded-full flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploading ? (
                  <svg className="animate-spin w-7 h-7 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                ) : (
                  <>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <span className="text-white text-xs mt-1 font-medium">Change</span>
                  </>
                )}
              </div>

              {/* Small camera badge */}
              <div className="absolute bottom-0 right-0 w-7 h-7 bg-whatsapp-green rounded-full flex items-center justify-center shadow-md border-2 border-white">
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                </svg>
              </div>
            </div>

            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
            <p className="text-xs text-gray-400 mt-2">Tap to change · Max 5 MB</p>
          </div>

          {/* Upload button (extra for mobile touch) */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-2 py-2.5 sm:py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-whatsapp-green hover:text-whatsapp-green transition-colors mb-5 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Uploading…
              </>
            ) : (
              <>
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                </svg>
                Upload New Profile Picture
              </>
            )}
          </button>

          {/* User info */}
          <div className="space-y-2 sm:space-y-3">
            {[
              { icon: '👤', label: 'Full Name',  value: user?.name },
              { icon: '@',  label: 'Username',   value: `@${user?.username}` },
              { icon: user?.role === 'admin' ? '🛡️' : '🎓', label: 'Role', value: user?.role },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3">
                <span className="text-lg sm:text-xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                  <p className="text-gray-800 font-semibold text-sm sm:text-base capitalize">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Avatar Color Picker ─────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
          <h2 className="text-sm font-bold text-gray-700 mb-1 flex items-center gap-2">
            🎨 Avatar Colour
            {colorSaving && (
              <svg className="animate-spin w-4 h-4 text-whatsapp-green" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            )}
          </h2>
          <p className="text-xs text-gray-400 mb-3">Shown when no profile picture is set</p>
          <div className="grid grid-cols-6 gap-2">
            {AVATAR_COLORS.map(color => (
              <button
                key={color}
                onClick={() => handleColorChange(color)}
                disabled={colorSaving}
                className={`w-full aspect-square rounded-full border-4 transition-all shadow-sm hover:scale-110 active:scale-95 ${
                  user?.avatar_color === color
                    ? 'border-gray-800 scale-110'
                    : 'border-white hover:border-gray-300'
                }`}
                style={{ backgroundColor: color }}
                aria-label={`Select colour ${color}`}
                title={color}
              />
            ))}
          </div>
        </div>

        {/* ── Install App Banner ──────────────────────────────────────────── */}
        {!isInstalled && (
          <div className="bg-gradient-to-br from-whatsapp-darker to-whatsapp-dark rounded-2xl p-4 sm:p-5 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2 sm:mb-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-xl flex items-center justify-center text-lg sm:text-xl flex-shrink-0">
                📱
              </div>
              <div>
                <h3 className="font-bold text-sm sm:text-base">Install App</h3>
                <p className="text-green-200 text-xs">Use GS Mwendo on your phone or desktop</p>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-green-100 mb-3 sm:mb-4">
              Add this app to your home screen for a native experience — works offline too.
            </p>
            <div className="flex gap-2 flex-wrap">
              {installPrompt && (
                <button onClick={handleInstall}
                  className="flex items-center gap-2 bg-whatsapp-green text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-green-400 transition-colors shadow">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                  </svg>
                  Install Now
                </button>
              )}
              <Link to="/install"
                className="flex items-center gap-2 bg-white text-whatsapp-darker px-4 py-2 rounded-xl text-sm font-bold hover:bg-green-50 transition-colors">
                📖 How to Install
              </Link>
            </div>
          </div>
        )}

        {isInstalled && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
            <span className="text-2xl">✅</span>
            <p className="text-green-700 font-bold text-sm mt-1">App is installed!</p>
            <p className="text-green-600 text-xs">You're using GS Mwendo as a native app.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
